// src/pages/WilayahPage.jsx

import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { 
  Title, Grid, Paper, Text, ScrollArea, Button, Group, Alert, Loader, Center,
  NavLink,      // <-- Komponen Modern
  ActionIcon,   // <-- Komponen Modern
  Tooltip       
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPencil, IconTrash, IconPlus } from "@tabler/icons-react"; 
import { notifications } from "@mantine/notifications"; 

// Impor 3 Form Modal
import KotaForm from "./KotaForm"; 
import KecamatanForm from "./KecamatanForm";
import KelurahanForm from "./KelurahanForm";

/**
 * Komponen baru: Daftar Wilayah
 * Ini adalah pengganti tabel yang modern
 */
const WilayahList = ({ title, data, onRowClick, selectedId, onAdd, onEdit, onDelete, loading }) => (
  <Paper withBorder p="md" shadow="sm">
    <Group justify="space-between" mb="md">
      <Text fw={700}>{title}</Text>
      <Tooltip label={`Tambah ${title.split(' ')[1]} Baru`}>
        <ActionIcon variant="light" color="blue" onClick={onAdd}>
          <IconPlus size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
    
    <ScrollArea h={400}>
      {loading && <Center><Loader size="xs" /></Center>}
      
      {!loading && data.length === 0 && (
        <Text c="dimmed" ta="center" size="sm" mt="lg">
          {title === "Daftar Kota" ? "Belum ada data." : "Pilih data dari tabel di kiri."}
        </Text>
      )}

      {data.map((item) => (
        <NavLink
          key={item.id}
          label={item.nama_kota || item.nama_kecamatan || item.nama_kelurahan}
          onClick={() => onRowClick(item)}
          active={item.id === selectedId} // Highlight biru
          variant="subtle"
          
          rightSection={
            <Group gap="xs" wrap="nowrap">
              <Tooltip label="Edit">
                <ActionIcon 
                  size="sm" 
                  variant="light" 
                  color="blue" 
                  onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                >
                  <IconPencil size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Hapus">
                <ActionIcon 
                  size="sm" 
                  variant="light" 
                  color="red" 
                  onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          }
        />
      ))}
    </ScrollArea>
  </Paper>
);


// --- Halaman Utama ---
export default function WilayahPage() {
  // ... (Semua state dan fungsi fetch/handler biarkan sama seperti sebelumnya) ...
  const [kotaList, setKotaList] = useState([]);
  const [kecamatanList, setKecamatanList] = useState([]);
  const [kelurahanList, setKelurahanList] = useState([]);
  
  const [selectedKota, setSelectedKota] = useState(null);
  const [selectedKecamatan, setSelectedKecamatan] = useState(null);
  
  const [loadingKota, setLoadingKota] = useState(true);
  const [loadingKec, setLoadingKec] = useState(false);
  const [loadingKel, setLoadingKel] = useState(false);
  
  const [modalState, setModalState] = useState({ type: null, data: null }); 

  const fetchKota = async () => {
    setLoadingKota(true);
    try {
      const response = await api.get("/admin/kota"); // (Pastikan rute ini benar)
      setKotaList(response.data);
    } catch (err) {
      notifications.show({ title: 'Error', message: 'Gagal mengambil data Kota.', color: 'red' });
    }
    setLoadingKota(false);
  };

  useEffect(() => {
    fetchKota();
  }, []);

  const handleKotaClick = (kota) => {
    if (selectedKota?.id === kota.id) return; 
    setSelectedKota(kota);
    setSelectedKecamatan(null); 
    setKelurahanList([]); 
    setLoadingKec(true);
    api.get(`/wilayah/kecamatan/${kota.id}`)
      .then(res => setKecamatanList(res.data))
      .catch(() => notifications.show({ title: 'Error', message: 'Gagal mengambil data Kecamatan.', color: 'red' }))
      .finally(() => setLoadingKec(false));
  };

  const handleKecamatanClick = (kecamatan) => {
    if (selectedKecamatan?.id === kecamatan.id) return;
    setSelectedKecamatan(kecamatan);
    setLoadingKel(true);
    api.get(`/wilayah/kelurahan/${kecamatan.id}`)
      .then(res => setKelurahanList(res.data))
      .catch(() => notifications.show({ title: 'Error', message: 'Gagal mengambil data Kelurahan.', color: 'red' }))
      .finally(() => setLoadingKel(false));
  };
  
  // (Handler Tambah/Edit/Delete dan SaveSuccess biarkan sama)
  const handleAddKota = () => setModalState({ type: 'kota', data: null });
  const handleEditKota = (kota) => setModalState({ type: 'kota', data: kota });
  const handleDeleteKota = async (id) => {
    if (window.confirm("Yakin hapus kota ini?")) {
      try {
        await api.delete(`/admin/kota/${id}`);
        notifications.show({ title: 'Sukses', message: 'Kota berhasil dihapus.', color: 'green' });
        fetchKota(); 
        setSelectedKota(null);
        setSelectedKecamatan(null);
        setKecamatanList([]);
        setKelurahanList([]);
      } catch (e) { 
        notifications.show({ title: 'Error', message: e.response?.data?.message || 'Gagal hapus kota.', color: 'red' });
      }
    }
  };

  const handleAddKecamatan = () => {
    if (!selectedKota) {
      notifications.show({ title: 'Info', message: 'Pilih Kota terlebih dahulu!', color: 'blue' });
      return;
    }
    setModalState({ type: 'kecamatan', data: { kota_id: selectedKota.id } });
  };
  const handleEditKecamatan = (kec) => setModalState({ type: 'kecamatan', data: kec });
  const handleDeleteKecamatan = async (id) => {
    if (window.confirm("Yakin hapus kecamatan ini?")) {
      try {
        await api.delete(`/admin/kecamatan/${id}`);
        notifications.show({ title: 'Sukses', message: 'Kecamatan berhasil dihapus.', color: 'green' });
        handleKotaClick(selectedKota); 
      } catch (e) { 
        notifications.show({ title: 'Error', message: e.response?.data?.message || 'Gagal hapus kecamatan.', color: 'red' });
      }
    }
  };

  const handleAddKelurahan = () => {
    if (!selectedKecamatan) {
      notifications.show({ title: 'Info', message: 'Pilih Kecamatan terlebih dahulu!', color: 'blue' });
      return;
    }
    setModalState({ type: 'kelurahan', data: { kecamatan_id: selectedKecamatan.id } });
  };
  const handleEditKelurahan = (kel) => setModalState({ type: 'kelurahan', data: kel });
  const handleDeleteKelurahan = async (id) => {
     if (window.confirm("Yakin hapus kelurahan ini?")) {
      try {
        await api.delete(`/admin/kelurahan/${id}`);
        notifications.show({ title: 'Sukses', message: 'Kelurahan berhasil dihapus.', color: 'green' });
        handleKecamatanClick(selectedKecamatan); 
      } catch (e) { 
        notifications.show({ title: 'Error', message: e.response?.data?.message || 'Gagal hapus kelurahan.', color: 'red' });
      }
    }
  };

  const handleSaveSuccess = () => {
    const type = modalState.type;
    notifications.show({ title: 'Sukses', message: `Data ${type} berhasil disimpan.`, color: 'green' });
    setModalState({ type: null, data: null }); 
    if (type === 'kota') {
      fetchKota();
    } else if (type === 'kecamatan' && selectedKota) {
      handleKotaClick(selectedKota); 
    } else if (type === 'kelurahan' && selectedKecamatan) {
      handleKecamatanClick(selectedKecamatan); 
    }
  };
  
  return (
    <>
      <Title order={2} mb="md">Manajemen Wilayah</Title>
      
      <Grid>
        {/* Kolom 1: KOTA */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <WilayahList
            title="Daftar Kota"
            data={kotaList}
            onRowClick={handleKotaClick}
            selectedId={selectedKota?.id}
            onAdd={handleAddKota}
            onEdit={handleEditKota}
            onDelete={handleDeleteKota}
            loading={loadingKota}
          />
        </Grid.Col>

        {/* Kolom 2: KECAMATAN */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <WilayahList
            title={`Kecamatan di ${selectedKota ? selectedKota.nama_kota : '...'}`}
            data={kecamatanList}
            onRowClick={handleKecamatanClick}
            selectedId={selectedKecamatan?.id}
            onAdd={handleAddKecamatan}
            onEdit={handleEditKecamatan}
            onDelete={handleDeleteKecamatan}
            loading={loadingKec}
          />
        </Grid.Col>

        {/* Kolom 3: KELURAHAN */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <WilayahList
            title={`Kelurahan di ${selectedKecamatan ? selectedKecamatan.nama_kecamatan : '...'}`}
            data={kelurahanList}
            onRowClick={() => {}} // Tidak ada aksi klik
            selectedId={null}
            onAdd={handleAddKelurahan}
            onEdit={handleEditKelurahan}
            onDelete={handleDeleteKelurahan}
            loading={loadingKel}
          />
        </Grid.Col>
      </Grid>
      
      {/* --- MODAL FORM --- */}
      <KotaForm
        opened={modalState.type === 'kota'}
        onClose={() => setModalState({ type: null, data: null })}
        onSuccess={handleSaveSuccess}
        kota={modalState.data}
      />
      <KecamatanForm
        opened={modalState.type === 'kecamatan'}
        onClose={() => setModalState({ type: null, data: null })}
        onSuccess={handleSaveSuccess}
        kecamatan={modalState.data}
      />
      <KelurahanForm
        opened={modalState.type === 'kelurahan'}
        onClose={() => setModalState({ type: null, data: null })}
        onSuccess={handleSaveSuccess}
        kelurahan={modalState.data}
      />
    </>
  );
}